import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import { Box, Button, Typography } from "@mui/material"
import LoadingOverlay from "../../components/LoadingOverlay"
import SBPIconNoExt from "../../components/SBPIconNoExt"

import { UpdateDefaultBankProps } from "./props"
import { useGetUpdateDefaultBankRequestStatus } from "../../api/hooks/useGetUpdateDefaultBankRequestStatus"
import { useCreateUpdateDefaultBankRequest } from "../../api/hooks/useCreateUpdateDefaultBankRequest"
import { useConfirmUpdateDefaultBankRequest } from "../../api/hooks/useConfirmUpdateDefaultBankRequest"
import { UpdateDefaultBankRequestAction, UpdateDefaultBankRequestStatus } from "../../api/interface"
import { setNextStep } from "../../store/slices/routingSlice/routingSlice"
import { settingsSBPSteps, STEPS_NAME } from "../../store/slices/routingSlice/flowsSteps"
import { CodeTitle, ContentContainer, IconContainer, RepeatCodeTitle, StyledMuiOtp } from "./styled"
import { AuthAgreement, AuthSubtitle, AuthTitle, CloseButton } from "../../components/styled"
import { ROUTES_PATHS } from "../../utils/constants"
import ErrorHandlerModal from "../../components/Modal/ErrorHandlerModal"
import { formatPhoneNumber } from "../../utils/formatters"

const UpdateDefaultBank = ({phoneNumber, action}: UpdateDefaultBankProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [requestId, setRequestId] = useState("")
  const [enablePolling, setEnablePolling] = useState(false)
  const [otp, setOtp] = useState("")

  const [openModal, setOpenModal] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const [countdown, setCountdown] = useState(30)
  const [incorrectPasswordLimit, setIncorrectPasswordLimit] = useState(3)

  const isCalled = useRef(false)

  const { data: request } = useGetUpdateDefaultBankRequestStatus(requestId, enablePolling)
  const { mutateAsync: createRequest } = useCreateUpdateDefaultBankRequest()
  const { mutateAsync: confirmRequest } = useConfirmUpdateDefaultBankRequest()

  const successStep = action === UpdateDefaultBankRequestAction.SET
    ? STEPS_NAME.sbpSettings.setDefaultBankSuccess : STEPS_NAME.sbpSettings.deleteDefaultBankSuccess

  const failStep = action === UpdateDefaultBankRequestAction.SET
    ? STEPS_NAME.sbpSettings.setDefaultBankFail : STEPS_NAME.sbpSettings.deleteDefaultBankFail

  const handleInputChange = (value: string) => {
    if (!incorrectPasswordLimit) {
      setIncorrectPasswordLimit(3)
    }
    const filteredValue = value.replace(/[^0-9]/g, "")
    setOtp(filteredValue)
  }

  useEffect(() => {
    switch (request?.status) {
      case UpdateDefaultBankRequestStatus.CREATED:
        setEnablePolling(false)
        setIsLoading(false)
        break

      case UpdateDefaultBankRequestStatus.CREATE_ERROR:
        setOpenModal(true)
        setEnablePolling(false)
        setIsLoading(false)
        break

      case UpdateDefaultBankRequestStatus.CONFIRMING_ERROR:
        setEnablePolling(false)
        setIsLoading(false)
        setIncorrectPasswordLimit(incorrectPasswordLimit - 1)
        if (!(incorrectPasswordLimit - 1)) {
          setError(false)
          return
        }
        setOtp("")
        setError(true)
        break

      case UpdateDefaultBankRequestStatus.CONFIRMED:
        dispatch(setNextStep(successStep))
        setOtp("")
        setEnablePolling(false)
        setIsLoading(false)
        break

      case UpdateDefaultBankRequestStatus.CONFIRM_ERROR:
        setEnablePolling(false)
        setIsLoading(false)
        setIncorrectPasswordLimit(() => {
          const newLimit = 0
          setError(false)
          return newLimit
        })
        dispatch(setNextStep(failStep))
        break

      default:
        break
    }
  }, [request])

  useEffect(() => {
    ;(async () => {
      if (!isCalled.current) {
        isCalled.current = true
        const responce = await createRequest({ action })
        setRequestId(responce?.requestId)
      }
    })()
  }, [createRequest])

  useEffect(() => {
    if (requestId) {
      setEnablePolling(true)
    }
  }, [requestId])

  useEffect(() => {
    ;(async () => {
      if (otp.length === 6) {
        setIsLoading(true)
        await confirmRequest({ requestId, otp })
        setEnablePolling(true)
      }
    })()
  }, [otp])

  useEffect(() => {
    const timer = setInterval(() => {
      if (countdown > 0) {
        setCountdown(countdown - 1)
      } else {
        clearInterval(timer)
      }
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [countdown])

  if (isLoading) {
    return <LoadingOverlay />
  }

  const getTitle = () => {
    if (action === UpdateDefaultBankRequestAction.DELETE) {
      return "Удаление в СБП Банка по умолчанию"
    }
    return "Код подтверждения"
  }

  const getDescription = () => {
    if (action === UpdateDefaultBankRequestAction.DELETE) {
      return `При переводе через СБП отправителю не будет предложен банк по умолчанию
        для получения переводов`
    }

    return `При переводе через СБП РНКО «Единая касса» будет предложена отправителю
        в качестве вашего банка по умолчанию для получения перевода`
  }

  return (
    <Box sx={ContentContainer}>
      <Typography variant="h3" sx={AuthTitle}>
        {getTitle()}
      </Typography>
      <Typography variant="h7Pale" sx={AuthAgreement}>
        {getDescription()}
      </Typography>
      <Typography variant="h6" sx={AuthSubtitle}>
        Код подтверждения отправлен на номер {formatPhoneNumber(phoneNumber)}
      </Typography>
      <Typography variant="h4Bold" sx={CodeTitle}>
        Код из СМС
      </Typography>
      <StyledMuiOtp
        autoFocus
        length={6}
        onChange={handleInputChange}
        value={otp}
        error={error}
        TextFieldsProps={{
          disabled: Boolean(!incorrectPasswordLimit),
          variant: "standard",
          type: "number",
        }}
      />
      {!incorrectPasswordLimit && (
        <Typography variant="h6" sx={RepeatCodeTitle}>
          Лимит по отправке смс с кодом исчерпан
        </Typography>
      )}
      <IconContainer>
        <SBPIconNoExt size={60} />
      </IconContainer>
      {request?.status === UpdateDefaultBankRequestStatus.CONFIRM_ERROR && (
        <Button
          variant="contained"
          color="secondary"
          sx={CloseButton}
          onClick={() => {
            navigate(`/${ROUTES_PATHS.main}`)
          }}
        >
          Закрыть
        </Button>
      )}
      <ErrorHandlerModal
        open={openModal}
        toggleMOdal={() => {
          setOpenModal(false)
          dispatch(setNextStep(settingsSBPSteps.sbpSettings.name))
        }}
      />
    </Box>
  )
}

export default UpdateDefaultBank